import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {mergeWorkspaceTasks} from "../../system/workspace-graph-slice";
import Task from "./task";
import {isAfter, isPast} from "../../system/utils";
import {DateObject} from "react-multi-date-picker";
import {normalizeMembershipEntity, prepareMembershipPayload} from "../workspace-membership/workspace-membership-identity";
import {buildMyTasksScopeFilter} from "./my-tasks-scope";
import {getTaskTimeRemain} from "./task-time-remain";
import {isRecurring, toRecurrencePayload} from "../../pages/tasks/components/task-recurrence-model";

function buildTaskQuery() {
    return {
        populate: {
            workspace: {fields: ["id", "documentId"]},
            creatorUser: {populate: {member: {populate: ["profileImage"]}}},
            assignee: {populate: {member: {populate: ["profileImage"]}}},
            members: {populate: {member: {populate: ["profileImage"]}}},
            archivedBy: {populate: {member: {populate: ["profileImage"]}}},
            projects: {
                populate: {
                    deliverables: {
                        populate: {
                            project: {fields: ["id"]}
                        }
                    }
                }
            },
            task_type: true,
            series: true,
            childrenTasks: {
                populate: {
                    assignee: {populate: {member: {populate: ["profileImage"]}}}
                }
            },
            parentTask: {
                fields: ["title"],
                populate: {
                    parentTask: {
                        fields: ["title"],
                        populate: {
                            parentTask: {
                                fields: ["title"],
                                populate: {
                                    parentTask: {
                                        fields: ["title"],
                                        populate: {
                                            parentTask: {
                                                fields: ["title"],
                                                populate: {
                                                    parentTask: {
                                                        fields: ["title"],
                                                        populate: {
                                                            parentTask: {
                                                                fields: ["title"],
                                                                populate: {
                                                                    parentTask: {
                                                                        fields: ["title"]
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            attachments: {populate: "*"},
            deliverables: {
                populate: {
                    project: {fields: ["id"]}
                }
            },
        }
    };
}

function markTaskDetails(task) {
    return task ? {...task, __detailsLoaded: true} : null;
}

const inFlightTaskListRequests = new Map();

export function buildTaskListQuery(andFilters) {
    return {
        filters: {$and: andFilters},
        fields: [
            "title",
            "taskStatus",
            "startAt",
            "dueDate",
            "duration",
            "actualActivitySeconds",
            "scheduleType",
            "sortIndex",
            "freeBoardColumnId",
            "freeBoardSortIndex",
            "checklist",
            "description",
            "archivedAt",
            "doneAt",
            "sequenceNumber",
            "scheduledStartAt",
            "scheduledDueAt",
            "occurrenceStatus",
            "hasException",
            "isPrivate",
            "priority",
            "tags",
            "createdAt",
            "updatedAt",
        ],
        sort: ["dueDate:asc"],
        populate: {
            series: true,
            workspace: {fields: ["id", "documentId"]},
            creatorUser: {populate: {member: {populate: ["profileImage"]}}},
            assignee: {populate: {member: {populate: ["profileImage"]}}},
            members: {populate: {member: {populate: ["profileImage"]}}},
            projects: {fields: ["id", "documentId", "title", "freeBoardColumns"]},
            task_type: true,
            childrenTasks: {
                fields: ["id", "documentId", "title", "taskStatus", "dueDate"],
                populate: {
                    assignee: {populate: {member: {populate: ["profileImage"]}}},
                },
            },
            parentTask: {fields: ["id", "documentId", "title"]},
            deliverables: {
                fields: ["id", "documentId", "title"],
                populate: {
                    project: {fields: ["id", "documentId"]},
                },
            },
            task_comments: {fields: ["id", "documentId"]},
            attachments: {fields: ["id", "documentId", "name"]},
        },
        pagination: {pageSize: 99999, withCount: false},
    };
}

export function runTaskListSingleFlight(key, requestFactory) {
    const existing = inFlightTaskListRequests.get(key);
    if (existing) return existing;

    const request = Promise.resolve().then(requestFactory);
    inFlightTaskListRequests.set(key, request);
    request.finally(() => {
        if (inFlightTaskListRequests.get(key) === request) {
            inFlightTaskListRequests.delete(key);
        }
    }).catch(() => {});
    return request;
}

export function useTask() {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const {workspaceMembershipData} = useSelector(state => state.app);
    const memberships = workspaceMembershipData?.workspace?.workspace_memberships || [];
    const normalizeTask = (item) => normalizeMembershipEntity(item, "task", memberships);

    const getTasks = async (projectId = null, workspaceId = null, options = {}) => {
        const membershipDocumentId = workspaceMembershipData?.documentId;
        if (!workspaceId || !membershipDocumentId) return [];

        try {
            let andFilters = [
                { workspace: { $eq: workspaceId } },
                { archivedAt: { $null: true } },
            ];

            // دسترسی پروژه یا عمومی
            if (projectId) {
                andFilters.push({
                    projects: { $in: projectId }
                });
            } else if (options.scope === "my-tasks") {
                andFilters = buildMyTasksScopeFilter(workspaceId, membershipDocumentId).$and;
            } else {
                andFilters.push({
                    $or: [
                        // 1. اگر assignee وجود دارد، فقط او مهم است
                        {
                            assignee: {documentId: {$eq: membershipDocumentId}}
                        },

                        // 2. اگر assignee وجود ندارد، creatorUser جایگزین می‌شود
                        {
                            $and: [
                                { assignee: { $null: true } },
                                { creatorUser: {documentId: {$eq: membershipDocumentId}} }
                            ]
                        },

                        // (اختیاری ولی منطقی) عضویت تیمی
                        {
                            members: {documentId: {$eq: membershipDocumentId}}
                        }
                    ]
                });
            }

            const requestKey = [workspaceId, membershipDocumentId, projectId || "all", options.scope || "default"].map(String).join(":");
            const _data = await runTaskListSingleFlight(requestKey, () => (
                new Task().getAll(buildTaskListQuery(andFilters))
            ));
            return (_data?.data || []).map(normalizeTask);

        } catch (e) {
            if (options.throwOnError) throw e;
            console.error(e);
            return [];
        }
    };
    const findTasks = async (query)=>{
        try {
            const task = new Task();
            const _data = await task.getAll(query);
            return (_data.data || []).map(normalizeTask)
        }catch (e){

        }
    }

    // Completing a repeating task makes the server create the next occurrence (after-completion mode) or fill the
    // window (on-schedule mode); fetch the later occurrences so the lists show them without a reload.
    const loadNextOccurrences = async (completed) => {
        try {
            const res = await new Task().getAll(buildTaskListQuery([
                {series: {documentId: {$eq: completed.series.documentId}}},
                {sequenceNumber: {$gt: completed.sequenceNumber ?? 0}},
                {archivedAt: {$null: true}},
            ]));
            dispatch(mergeWorkspaceTasks((res?.data || []).map(normalizeTask)));
        } catch (e) {
            // The next occurrence still appears after the next list refresh.
        }
    };

    // `recurrence` (UI value) is converted to the API payload; `recurrenceScope` ("this" | "following" | "all")
    // is sent as `?scope=`. Both keys are consumed here so callers can keep passing plain update objects.
    const splitRecurrenceFields = (taskData) => {
        if (!taskData || typeof taskData !== "object") return {data: taskData, scope: null};
        const {recurrenceScope, ...data} = taskData;
        if (Object.prototype.hasOwnProperty.call(data, "recurrence")) {
            data.recurrence = isRecurring(data.recurrence) ? toRecurrencePayload(data.recurrence) : null;
        }
        return {data, scope: recurrenceScope || null};
    };

    const updateTask = async (taskId, taskData, options = {})=>{
        try {
            const task = new Task();
            const {data, scope} = splitRecurrenceFields(taskData);
            const payload = prepareMembershipPayload("task", data, memberships, {
                operation: "update",
                currentMembership: workspaceMembershipData,
            });
            const _data = await task.update(taskId, payload, {...buildTaskQuery(), ...(scope ? {scope} : {})});
            const updated = markTaskDetails(normalizeTask(_data.data));
            if (data?.taskStatus === "done" && updated?.series?.documentId) {
                await loadNextOccurrences(updated);
            }
            return updated
        }catch (e){
            if (options.throwOnError) throw e;
        }
    }

    // Recurring tasks: delete this / this and following / all occurrences (hard delete of the open ones).
    const deleteTask = async (taskId, scope = "this")=>{
        const task = new Task();
        return task.deleteWithScope(taskId, scope);
    }

    const createTask = async (taskData, options = {})=>{
        try {
            const task = new Task();
            const {data} = splitRecurrenceFields(taskData);
            if (data && data.recurrence === null) delete data.recurrence;
            const payload = prepareMembershipPayload("task", data, memberships, {
                operation: "create",
                currentMembership: workspaceMembershipData,
            });
            const _data = await task.create(payload, buildTaskQuery());
            return markTaskDetails(normalizeTask(_data.data))
        }catch (e){
            if (options.throwOnError) throw e;
        }
    }

    const prepareTable = ({actions, config})=>{
        const fields=["title", "taskStatus", "assignee", "dueDate"]//Object.keys(DeviceEntity.fields);
        const value=(item)=>{
            return(
                <div className="flex gap-1 align-items-center text-xs">
                    <span className="pi pi-eye cursor-pointer hover:surface-100 border-circle p-3" onClick={()=>{}}/>
                    {/*<span className="pi pi-file-edit cursor-pointer hover:surface-100 border-circle p-3" onClick={()=>{actions.onEditClicked({value:item})}}/>*/}
                    {/*<span className={`pi pi-power-off cursor-pointer hover:surface-100 border-circle p-3 ${item.kphwms_isonline?"text-red-500":"text-green-500"}`} onClick={()=>{actions.onPowerOffClicked({value:item})}}/>*/}
                    {/*<span className="pi pi-sync cursor-pointer hover:surface-100 border-circle p-3" onClick={()=>{console.log(item)}}/>*/}
                    {/*<span className="pi pi-trash cursor-pointer hover:surface-100 border-circle p-3" onClick={()=>{actions.onDeleteClicked({value:item})}}/>*/}
                </div>
            )
        }
        const fieldsConfig = {
            title:{alias:"task-title", type:"TEXT", "class-name":"flex-1 pl-3 pr-3", translate:true,},
            taskStatus:{alias:"status", type:"TEXT", "class-name":"flex-1 pl-3 pr-3", translate:true,},
            assignee:{alias:"assignee", type:"AVATAR_GROUP", "class-name":"flex-1 pl-3 pr-3", translate:true,},
            dueDate:{alias:"dueDate", type:"TIME_SINCE", "class-name":"flex-1 pl-3 pr-3", translate:true,},
            actions:{type:"ACTIONS", value, translate:true, "class-name":"w-3rem"},
        }

        return {fields, fieldsConfig}
    }

    const filter = (tasks, condition, filterFunction)=>{
        const _tasks = Array.isArray(tasks) ? tasks : [];
        const futureLimit = new DateObject().add(7,"days").toUnix();
        switch (condition) {
            case "overdue": return _tasks.filter(t=>{ const timeRemain = getTaskTimeRemain(t); return t.assignee&&t.taskStatus!=="done"&&timeRemain==="overdue"; })
            case "today": return _tasks.filter(t=>{ const timeRemain = getTaskTimeRemain(t); return t.assignee&&t.taskStatus!=="done"&&timeRemain==="today"; })
            case "this-week": return _tasks.filter(t=>{ const timeRemain = getTaskTimeRemain(t); return t.assignee&&t.taskStatus!=="done"&&(timeRemain==="this-week"||timeRemain==="two-other-days"||timeRemain==="tomorrow"); })
            case "next-week":return _tasks.filter(t => { const timeRemain = getTaskTimeRemain(t); return t.assignee && t.taskStatus !== "done" && timeRemain === "next-week"; })
            case "near": return _tasks.filter(t=>{ const timeRemain = getTaskTimeRemain(t); return t.assignee&&t.taskStatus!=="done"&&(timeRemain==="two-other-days"||timeRemain==="tomorrow"); })
            case "future": return _tasks.filter(t=>t.assignee&&t.taskStatus!=="done"&&isAfter(t.dueDateUnix ?? new Date(t.dueDate).getTime()/1000, 1, "days", futureLimit))
            case "no-due": return _tasks.filter(t=>t.taskStatus!=="done"&&(!t.dueDate||!t.assignee))
            case "todo": return _tasks.filter(t=>t.taskStatus==="todo")
            case "doing": return _tasks.filter(t=>t.taskStatus==="doing")
            case "blocked": return _tasks.filter(t=>t.taskStatus==="blocked")
            case "done": return _tasks.filter(t=>t.taskStatus==="done")
            case "not-done": return _tasks.filter(t=>t.taskStatus!=="done")
            default: return filterFunction?_tasks.filter(filterFunction):_tasks;
        }
    }

    const relationKey = (value)=>{
        const key = value?.id ?? value?.documentId;
        return key === null || key === undefined ? null : String(key);
    };

    const filterByDeliverable = (tasks, deliverable, reverse = false) => {
        const list = Array.isArray(tasks) ? tasks : [];
        const deliverableKey = relationKey(deliverable);
        const projectKey = relationKey(deliverable?.project);

        const isLinkedToDeliverable = (task)=>(task.deliverables || []).some((item)=>{
            const key = relationKey(item);
            return Boolean(key && key === deliverableKey);
        });

        const isLinkedToOtherInSameProject = (task)=>(task.deliverables || []).some((item)=>{
            const key = relationKey(item);
            if (key && key === deliverableKey) return false;
            if (!projectKey) return false;
            return relationKey(item?.project) === projectKey;
        });

        return list
            .filter((task)=>!isLinkedToOtherInSameProject(task))
            .filter((task)=> reverse ? !isLinkedToDeliverable(task) : isLinkedToDeliverable(task));
    }

    const tasksPercent = (tasks, condition, filterFunction) => {
        const _tasks = Array.isArray(tasks) ? tasks : [];
        const filteredTasks = filter(_tasks, condition, filterFunction);
        const value = _tasks.length > 0 ? Math.round((filteredTasks.length / _tasks.length) * 100) : 0;

        return {
            value,
            percent: _tasks.length > 0 ? `${value}%` : "---",
        };
    };

    const getArchivedTask = async (documentId) => {
        const taskModel = new Task();

        const res = await taskModel.getById(documentId, buildTaskQuery());

        return markTaskDetails(normalizeTask(res?.data));
    };

    const getTask = async (documentId) => {
        if (!documentId) return null;

        try {
            const taskModel = new Task();
            const res = await taskModel.getById(documentId, buildTaskQuery());

            return markTaskDetails(normalizeTask(res?.data));
        } catch (e) {
            return null;
        }
    };

    return {getTasks, updateTask, createTask, deleteTask, findTasks, prepareTable, filter, filterByDeliverable, tasksPercent, getArchivedTask, getTask};
}
